//
//

/* eslint-disable no-bitwise */

import { mapGetters, mapMutations, mapActions } from "vuex";
import { date, debounce } from "quasar";
import { APP_DATE_DOT_FORMAT } from "@/assets/js/appConfig";
import i18n from "@/i18n/index";
import {
    DiagramTools,
    Diagram,
    DiagramComponent as ejsDiagram,
    Snapping,
    SymbolPaletteComponent as ejsSymbolpalette,
    LineRouting,
    UndoRedo,
    Rect,
    Keys,
    ConnectorBridging,
    ConnectorEditing,
    SelectorConstraints,
    AnnotationConstraints
} from "@syncfusion/ej2-vue-diagrams";
import shortId from "short-uuid";
import RuleTesting from "@/components/Diagrams/RuleTesting/RuleTesting.vue";
import pluginDescription from "@/components/Diagrams/sidebar/plugin-description.vue";
import canvasElements from "@/components/Diagrams/sidebar/canvas-elements.vue";
import AQModal from "@/components/Generics/AQModal/AQModal.vue";
import AQHistory from "@/components/Generics/AQHistory/AQHistory.vue";
import AQDialog from "@/components/Generics/AQDialog/AQDialog.vue";
import AQInput from "@/components/Generics/AQInput/AQInput.vue";
import { $http, eventBus, publishAppPermissions, infoToastPublish } from "@/assets/js/appFunc";
import AQButton from "@/components/Generics/AQButton/AQButton.vue";
import AQTooltip from "@/components/Generics/AQTooltip/AQTooltip.vue";

import { paletteGetNodeDefaults,
    curryGetNodeDefaults,
    getAutostartShape,
    getConnectorDefaults,
    dropNode,
    getSymbolInfo,
    ConnectorDraw,
    annotationConfig,
    paletteSetup,
    convertOldDiagram,
    revertToOldDiagram } from "./DiagramFunctions";
import { diagramConfig } from "./DiagramConfig";
import { paletteConfig } from "./PaletteConfig";

Diagram.Inject(ConnectorEditing);
Diagram.Inject(Snapping);
Diagram.Inject(LineRouting);
Diagram.Inject(UndoRedo);
Diagram.Inject(ConnectorBridging);

let diagramInstance, palette;

const config = {
    ...diagramConfig,
    commandManager: {
        commands: [
            {
                name: "copy",
                canExecute() { return false; }
            },
            {
                name: "paste",
                canExecute() { return false; }
            },
            {
                name: "nudgeUp",
                gesture: {
                    key: Keys.Up
                },
                execute() {
                    diagramInstance.nudge("Up", 16, 16);
                }
            },
            {
                name: "nudgeDown",
                gesture: {
                    key: Keys.Down
                },
                execute() {
                    diagramInstance.nudge("Down", 16, 16);
                }
            },
            {
                name: "nudgeRight",
                gesture: {
                    key: Keys.Right
                },
                execute() {
                    diagramInstance.nudge("Right", 16, 16);
                }
            },
            {
                name: "nudgeLeft",
                gesture: {
                    key: Keys.Left
                },
                execute() {
                    diagramInstance.nudge("Left", 16, 16);
                }
            }
        ]
    }
};

export default {
    name: "Syncfusion",
    components: {
        RuleTesting,
        pluginDescription,
        canvasElements,
        ejsDiagram,
        ejsSymbolpalette,
        AQModal,
        AQHistory,
        AQDialog,
        AQInput,
        AQButton,
        AQTooltip
    },
    data() {
        return {
            openCollapsible: false,
            pageName: "Diagrams",
            readonly: false,
            warningsList: [],
            isError: false,
            pluginDescriptionCard: false,
            isConnSelectWarning: false,
            selectedConnUuid: null,
            notifyUpdate: null,
            diagramWarnings: [],
            diagramErrors: [],
            zoomLevel: 100,
            connectorPending: false,
            customEventConnSelected: true,
            oneAtATimeParams: { readonly: false, oneAtATime: false },
            saveHistoryData: [],
            saveHistoryModal: false,
            saveHistoryCurrentPage: 0,
            saveHistoryHasMoreItems: true,
            saveHistoryActiveCommitId: null,
            currentPreviewProcess: null,
            previewDone: false,
            savePreviewToast: null,
            standaloneTypes: new Set(["publish", "delete", "recover"]),
            restoreConfirmationDialog: false,
            restoreDialogMessage: null,
            restoredProcessData: null,
            dirtyFromRestore: false,
            processPreview: null,
            processConfigPreview: null,
            openDialogDraftPreview: false,
            noteDialog: false,
            noteDialogTitle: null,
            confirmLabel: null,
            note: null,
            createNote: false,
            keyUpdate: 0,
            dialogAction: "",
            timeStampPreview: null,
            inPreviewMode: false,
            processRenamed: false,
            diagramSettings: {
                showGrid: true,
                showBridges: true,
                showLabels: true
            },
            settingsEnabled: true,
            showSettings: false
        };
    },
    computed: {
        ...mapGetters("diagrams", [
            "selectedPluginConfigData",
            "selectedPluginUuid",
            "diagramConfigData",
            "diagramData",
            "configCache",
            "scDetailsEvent",
            "scDetailsFunction",
            "getWarningsList",
            "getProcessWithError",
            "copyOfInitialContentDiagram",
            "getDiagramStateModifications",
            "copyOfInitialMessagesDiagam",
            "copyOfInitialConnectorsDiagram"
        ]),
        ...mapGetters("rules", ["showRuleTesting"]),
        ...mapGetters("apps", ["apps", "currentApp", "currentProcess", "processList", "getCurrentAppUserPermissions"]),
        ...mapGetters("main", ["getCurrentApplicationId", "getCurrentProcessId", "getDisableOnLoad", "interfaceDirty", "getNewProcessCreated"]),
        createNoteDisabled() {
            return !this.note && this.createNote;
        },
        paletteConfig() {
            return paletteConfig(this.currentApp.blockchainEnabled);
        },
        config() {
            return { readonly: this.readonly };
        },
        readonlyPermission() {
            return this.$route.params && this.$route.params.permissions && this.$route.params.permissions.readonly;
        }
    },
    watch: {
        getDiagramStateModifications: {
            handler(val) {
                if (val.length) {
                    this.$root.$emit("showCheckedSaveBtn", false);
                }
            },
            immediate: true,
            deep: true
        },
        diagramConfigData: {
            handler(val) {
                const formatActualDiagram = JSON.stringify(val.content);
                const formatActualDiagramConnectors = JSON.stringify(val.connectors);
                const formatActualDiagramMessage = JSON.stringify(val.messages);
                if (this.copyOfInitialContentDiagram && this.copyOfInitialContentDiagram !== formatActualDiagram) {
                    this.$root.$emit("showCheckedSaveBtn", false);
                }
                if (this.copyOfInitialConnectorsDiagram && this.copyOfInitialConnectorsDiagram !== formatActualDiagramConnectors) {
                    this.$root.$emit("showCheckedSaveBtn", false);
                }
                if (this.copyOfInitialMessagesDiagam && this.copyOfInitialMessagesDiagam !== formatActualDiagramMessage) {
                    this.$root.$emit("showCheckedSaveBtn", false);
                }
            },
            deep: true
        },
        "selectedPluginConfigData.label": {
            handler() {
                if ((this.selectedPluginConfigData.pluginType === "BLOCKCHAIN_EVENT_PLUGIN"
                || this.selectedPluginConfigData.pluginType === "START_BLOCKCHAIN_EVENT_PLUGIN")
                && this.scDetailsEvent && !this.scDetailsEvent.length) {
                    this.getBlockchainData("EVENT");
                }
                if (this.selectedPluginConfigData.pluginType === "BLOCKCHAIN_PLUGIN" && this.scDetailsFunction && !this.scDetailsFunction.length) {
                    this.getBlockchainData("FUNCTION");
                }
            }
        },
        "$backendWebsocketEvents.state.diagramChanged": function (actionDetails) {
            if (actionDetails) {
                const wsProcessId = actionDetails.data.split(":")[2];
                const processName = actionDetails.data.split(":")[4];
                if (!sessionStorage.getItem("wsActionUser") && this.$route.name === "process" && wsProcessId === this.getCurrentProcessId) {
                    if (!this.notifyUpdate) {
                        this.notifyUpdate = this.$toast.showNotify({
                            type: "info",
                            buttonLabel: `${this.$t("documents.refresh")}`,
                            message: this.$t("processes.processUpdatedRefresh", { name: processName }),
                            hasClose: false,
                            timeout: 0,
                            actionEvent: this.refreshPage
                        });
                    }
                }
            }
            sessionStorage.removeItem("wsActionUser");
            this.$backendWebsocketEvents.state.refreshProcessList = null;
        },
        getProcessWithError: {
            handler() {
                const checkErrorId = this.getProcessWithError && this.getProcessWithError.id === this.currentProcess.id;
                this.isError = checkErrorId;
                this.pluginDescriptionCard = checkErrorId;
            }
        },
        currentProcess: {
            handler() {
                if (!this.getDisableOnLoad && !this.processRenamed) {
                    this.DIAGRAM_VIEW_INITIALIZED();
                }
                this.processRenamed = false;
                const checkErrorId = this.getProcessWithError && this.getProcessWithError.id === this.currentProcess.id;
                this.isError = checkErrorId;
                this.pluginDescriptionCard = checkErrorId;
            },
            deep: true
        },
        getWarningsList: {
            handler(newVal) {
                this.warningsList = newVal;
            },
            immediate: true,
            deep: true
        }
    },
    created() {
        this.doChangeSelection = debounce(this.doChangeSelection, 30, true);
        this.diagramConfig = config;
        this.$store.dispatch("diagrams/getDiagramPlugins");
        this.$root.$on("bc__processes__open", this.crumbOpen);
        this.$root.$on("bc__currentProcess__rename", data => {
            this.renameSelectedProcess(data);
        });

        // eslint-disable-next-line consistent-return
        eventBus.$on("addConnector", data => {
            const found = diagramInstance.connectors.find(el => el.sourceID === data.from && el.targetID === data.to);
            if (found) {
                return data.callback(found.id);
            }
            const connector = {
                id: shortId.uuid(),
                type: "Orthogonal",
                sourceID: data.from,
                targetID: data.to,
                targetPortID: ""
            };
            diagramInstance.add(connector);
            data.callback(connector.id);
        });

        eventBus.$on("deleteConnector", uuid => {
            const conn = diagramInstance.connectors.find(el => el.id === uuid);
            if (conn) {
                diagramInstance.remove(conn);
            }
        });

        eventBus.$on("updateDesignPlugin", data => {
            this.setRecurrence(data);
        });

        eventBus.$on("addConnectorLabel", ({ uuid, label }) => {
            this.setConnectorLabelInDiagram(uuid, label);
        });

        this.$root.$off("processSave");
        this.$root.$off("oneAtATime-process");
        this.$root.$off("hideUpdateNotify");
        this.$root.$off("backendWebsocketEvents-publishedApp");
        this.$root.$off("startEditing");

        this.$root.$on("processSave", val => {
            this.$store.commit("main/setInterfaceDirty", false);
            this.save(val);
        });
        this.$root.$on("oneAtATime-process", oneAtATime => {
            this.oneAtATimeParams = { readonly: !this.$oneAtATime.state.$currentRoomEmpty, oneAtATime: !oneAtATime };
            this.checkPermissions();
        });
        this.$root.$on("startEditing", () => {
            this.DIAGRAM_VIEW_INITIALIZED();
        });
        this.$root.$on("hideUpdateNotify", () => {
            if (this.notifyUpdate) {
                this.notifyUpdate(); // dismiss toast
            }
        });
        this.$root.$on("previewSaveHistoryClose", routeName => {
            if (routeName === this.$route.name) {
                if (this.savePreviewToast) {
                    this.savePreviewToast();
                }
                this.setReadOnlyFalse();
                this.saveHistoryActiveCommitId = null;
                this.dirtyFromRestore = false;
                this.inPreviewMode = false;
                if (this.isError) {
                    this.$root.$emit("publishBtnWithError");
                }
                this.$store.commit("diagrams/diagramConfigData", this.processConfigPreview);
                this.$store.commit("diagrams/setCopyOfInitialConnectorsDiagram", JSON.stringify(this.processConfigPreview.connectors));
                this.$store.commit("diagrams/setCopyOfInitialMessagesDiagam", JSON.stringify(this.processConfigPreview.messages));
                this.$store.commit("diagrams/setCopyOfInitialContentDiagram", JSON.stringify(this.processConfigPreview.content));
                this.$store.commit("diagrams/diagramData", this.processPreview);
                Promise.resolve(this.loadDiagram(convertOldDiagram(this.processPreview, this.diagramSettings))).finally(() => {
                    this.resetCanlogDiagramFunction();
                });
                this.showHeaderBtnsAfterPreview();
            }
        });
        this.$root.$on("restore", () => {
            this.infoRestoreDialog();
            this.restoreConfirmationDialog = true;
        });
    },
    mounted() {
        palette = this.$refs.paletteObj.ej2Instances;
        diagramInstance = this.$refs.diagramObject.ej2Instances;
        diagramInstance.fitToPage();
        paletteSetup(palette);

        this.isError = this.getProcessWithError && this.getProcessWithError.id === this.currentProcess.id;
        if (this.isError) {
            this.pluginDescriptionCard = true;
        }
        const readonlyPermissOrMarked = this.readonlyPermission || this.currentProcess.markedForArchive;
        this.$store.commit("main/setSaveBtnVisible", readonlyPermissOrMarked);
        this.$root.$emit("setReadonlyTag", readonlyPermissOrMarked);
        if ((this.$route.params && !this.$route.params.processId) || this.getNewProcessCreated) {
            this.getAppProcesses({ applicationId: this.currentApp.id, searchTerm: "" }).then(rsp => {
                if (rsp.data) {
                    const copyOfRsp = [...rsp.data];
                    this.$store.commit("apps/setProcessList", copyOfRsp);
                    this.$root.$emit("showPublish", { noProcesses: !this.processList.length,
                        showPublish: this.publishAppPermissions(this.currentApp, this.getCurrentAppUserPermissions) });
                }
            });
            this.$store.commit("main/setNewProcessCreated", false);
        } else {
            this.$root.$emit("showPublish", { noProcesses: !this.processList.length,
                showPublish: this.publishAppPermissions(this.currentApp, this.getCurrentAppUserPermissions) });
        }

        this.$store.commit("rules/setShowInstanceTable", false);
        this.$store.commit("rules/setShowRuleTesting", false);
        this.DIAGRAM_VIEW_INITIALIZED();
        this.$root.$on("backendWebsocketEvents-publishedApp", data => {
            infoToastPublish(data);
            this.$root.$emit("changePublishError");
        });
        this.$root.$on("openNoteDialog", routeName => {
            if (routeName === this.$route.name) {
                this.getNoteData();
            }
        });
        this.$root.$on("openSaveHistory", routeName => {
            if (routeName === this.$route.name) {
                if (this.saveHistoryActiveCommitId) {
                    this.saveHistoryModal = true;
                    if (this.savePreviewToast) {
                        this.savePreviewToast();
                    }
                    this.$nextTick(() => {
                        const activePreviewListItem = document.querySelectorAll(".timeline_list_item.active.edit");
                        if (activePreviewListItem.length) {
                            activePreviewListItem[0].scrollIntoView();
                        }
                    });
                } else {
                    this.resetSaveHistory();
                    this.getSaveHistory();
                }
            }
        });
        this.$root.$on("showMoreSaveHistory", routeName => {
            if (routeName === this.$route.name) {
                this.saveHistoryCurrentPage += 1;
                this.getSaveHistory();
            }
        });
    },
    beforeDestroy() {
        this.$root.$off("bc__processes__open");
        this.$root.$off("bc__currentProcess__rename");
        this.$root.$off("processSave");
        this.$root.$off("oneAtATime-process");
        this.$root.$off("hideUpdateNotify");
        this.$root.$off("backendWebsocketEvents-publishedApp");
        this.$root.$off("startEditing");
        this.$root.$off("openSaveHistory");
        this.$root.$off("showMoreSaveHistory");
        this.$root.$off("previewSaveHistoryClose");
        this.$root.$emit("previewSaveHistoryHeaderClose", this.$route.name);
        this.dirtyFromRestore = false;
        this.inPreviewMode = false;
        this.setReadOnlyFalse();
        this.resetSaveHistory();

        this.$store.commit("diagrams/setCopyOfInitialContentDiagram", []);
        this.$store.commit("diagrams/setCopyOfInitialConnectorsDiagram", []);
        this.$store.commit("diagrams/setCopyOfInitialMessagesDiagam", []);
        this.$store.commit("diagrams/setSCDetailsEvent", []);
        this.pluginDescriptionCard = false;
        this.isError = false;
        if (this.notifyUpdate) {
            this.notifyUpdate();
            this.notifyUpdate = null;
        }
        this.$store.commit("main/setSaveBtnVisible", false);
    },
    methods: {
        ...mapMutations("rules", ["setShowRuleTesting", "setShowInstanceTable"]),
        ...mapMutations("apps", ["updateCurrentAppStatus"]),
        ...mapMutations("main", ["setInterfaceDirty", "setDisableOnLoad"]),
        ...mapMutations("diagrams", [
            "setLastPluginCreated",
            "setConnectorsValidation",
            "checkConnectorsUUIDs",
            "setCopyOfInitialContentDiagram",
            "setCopyOfInitialConnectorsDiagram",
            "setCopyOfInitialMessagesDiagam"
        ]),
        ...mapActions("diagrams", ["getScDetails", "getProcessHistoryPreviewData", "resetDiagramStoreData"]),
        ...mapActions("apps", ["getAppProcesses", "renameProcess"]),
        ...mapActions("components", ["getHistoryData", "getNote", "saveDeleteNote"]),
        paletteGetNodeDefaults,
        getConnectorDefaults,
        curryGetNodeDefaults,
        getSymbolInfo,
        setConnectorLabelInDiagram(id, content) {
            const connector = diagramInstance.connectors.find(el => el.id === id);
            if (connector) {
                diagramInstance.removeLabels(connector, connector.annotations);
                const annotation = {
                    ...annotationConfig,
                    content,
                    visibility: this.diagramSettings.showLabels,
                    constraints: this.diagramSettings.showLabels ? AnnotationConstraints.None : AnnotationConstraints.ReadOnly
                };
                annotation.style.fill = content ? "#F3F5F7" : "none";
                diagramInstance.addLabels(connector, [annotation]);
            }
            const stateConnector = this.diagramData.connectors.find(({ uuid }) => uuid === id);
            if (stateConnector) {
                stateConnector.label.value = content;
            }
        },
        setRecurrence({ pluginUUID, additionalOptions }) {
            const node = diagramInstance.nodes.find(({ id }) => id === pluginUUID);
            if (node) {
                node.addInfo = { additionalOptions: { ...additionalOptions } };
                node.shape.source = getAutostartShape(additionalOptions.hasRecurrence);
                diagramInstance.dataBind();
            }
        },
        dropNode(ev) {
            diagramInstance.nodes.forEach(node => {
                node.style.strokeColor = "none";
                node.style.strokeDashArray = "none";
            });
            dropNode(ev, this.readonly, diagramInstance);
            if (diagramInstance.nodes.length === 1) { // for the AD-22603 bug...
                setTimeout(() => { // force rerender of the first node...
                    diagramInstance.nodes[0].width -= 1;
                    diagramInstance.dataBind();
                    diagramInstance.nodes[0].width += 1;
                    diagramInstance.dataBind();
                }, 0);
            }
            this.$store.commit("diagrams/selectedPluginUuid", diagramInstance.nodes[diagramInstance.nodes.length - 1].id);
        },
        mouseEnter(ev) {
            if (ev.actualObject.propName === "connectors") {
                ev.actualObject.style.strokeWidth = 2.5;
                ev.actualObject.targetDecorator.style.strokeWidth = 1.7;
            } else if (!this.connectorPending && ev.actualObject.propName === "nodes") {
                const found = diagramInstance.selectedItems.nodes.find(node => node.id === ev.actualObject.id);
                if (!found) {
                    ev.actualObject.style.strokeColor = "#6C778B";
                }
            }
        },
        mouseLeave(ev) {
            if (ev.element.propName === "connectors") {
                ev.element.style.strokeWidth = 2;
                ev.element.targetDecorator.style.strokeWidth = 1.3;
            } else if (ev.element.propName === "nodes") {
                const found = diagramInstance.selectedItems.nodes.find(node => node.id === ev.element.id);
                if (!found) {
                    ev.element.style.strokeColor = "none";
                }
            }
        },
        historyChange(args) {
            if (args.action === "Undo" || args.action === "Redo") {
                this.$root.$emit("showCheckedSaveBtn", false);
                this.$store.commit("main/setInterfaceDirty", true);
            }
        },
        targetPointChange(ev) {
            if (ev.state === "Completed") {
                if (ev.targetNode || ev.targetPort) {
                    const { connector } = ev;
                    const stateConn = this.diagramData.connectors.find(({ uuid }) => uuid === connector.id);
                    stateConn.to = connector.targetID;
                    this.$store.commit("diagrams/setConfigData", {
                        uuid: connector.id,
                        value: connector.targetID,
                        property: "to",
                        type: "connector"
                    });
                }
            }
        },
        connectionChange(ev) {
            if (ev.state === "Changed") {
                if (ev.connectorEnd === "ConnectorTargetEnd") {
                    if (!ev.newValue.nodeId || ev.connector.sourceID === ev.connector.targetID) {
                        ev.connector.targetID = ev.oldValue.nodeId;
                        ev.connector.targetPortID = ev.oldValue.portId;
                        diagramInstance.dataBind();
                    }
                } else if (ev.connectorEnd === "ConnectorSourceEnd") {
                    ev.connector.sourceID = ev.oldValue.nodeId;
                    ev.connector.sourcePortID = ev.oldValue.portId;
                    ev.connector.segments = [{ type: "Orthogonal" }];
                    diagramInstance.dataBind();
                }
            }
        },
        annotationEdit(ev) {
            if (ev.newValue.length > 100) {
                ev.newValue = ev.newValue.substring(0, 100);
                ev.element.annotations[0].content = ev.newValue;
            }
            ev.element.annotations.forEach(ann => {
                ann.style.fill = ev.newValue ? "#F3F5F7" : "none";
                ann.visibility = ev.newValue ? this.diagramSettings.showLabels : false;
            });
            ev.element.annotations[0].constraints = this.diagramSettings.showLabels ? AnnotationConstraints.None : AnnotationConstraints.ReadOnly;
            let data;
            const {
                newValue: LABEL_VALUE,
                element: { id, propName }
            } = ev;
            if (propName === "nodes") {
                const node = this.diagramData.content.find(({ uuid }) => uuid === id);
                if (node) {
                    node.label.value = LABEL_VALUE;
                }
                data = {
                    payload: { LABEL_VALUE, PLUGIN_UUID: id },
                    type: "PLUGIN_LABEL_UPDATED"
                };
            } else if (propName === "connectors") {
                const connector = this.diagramData.connectors.find(({ uuid }) => uuid === id);
                if (connector) {
                    connector.label.value = LABEL_VALUE;
                }
                data = {
                    payload: { LABEL_VALUE, CONNECTOR_UUID: id },
                    type: "CONNECTOR_LABEL_UPDATED"
                };
            }
            this.onDiagramEvent(data);
        },
        selectionChange(ev) {
            diagramInstance.selectedItems.constraints = SelectorConstraints.None;

            if (ev.state === "Changed") {
                this.doChangeSelection(ev);
            }
        },
        doChangeSelection(ev) {
            const {
                newValue,
                newValue: [element]
            } = ev;

            let highlightConnectors = [];

            // remove all nodes highlight
            diagramInstance.nodes.forEach(node => {
                node.style.strokeColor = "none";
                node.ports.forEach(port => {
                    port.visibility = 12; // PortVisibility.Connect + hover <--- val
                    port.style.strokeColor = "#6C778B";
                });
            });

            if (newValue.length === 1) {
                // single select case
                if (element.propName === "nodes") {
                    diagramInstance.selectedItems.constraints = SelectorConstraints.UserHandle;
                    element.ports.forEach(port => {
                        port.visibility = 1; // PortVisibility.Visible <--- val
                        port.style.strokeColor = "#3E71D2";
                    });
                    highlightConnectors = [
                        ...(element.outEdges || []),
                        ...(element.inEdges || [])
                    ];
                    const checkForDynamicPlugin = () => {
                        let hasConnectorAvailable = true;
                        const { activities, events, gateways } = this.getDiagramExternalPlugins || {};
                        const allDynamicPlugins = [...(activities || []), ...(events || []), ...(gateways || [])];
                        allDynamicPlugins.forEach(plugin => {
                            if (plugin.id === element.properties.data.typeId && element.outEdges.length >= plugin.output) {
                                hasConnectorAvailable = false;
                            }
                        });
                        return hasConnectorAvailable;
                    };

                    // show user handle for unrestricted nodes
                    if (
                        element.properties.data.typeId !== "GENERAL_END_PLUGIN"
                        && (element.properties.data.typeId !== "GENERAL_START_PLUGIN" || element.outEdges.length < 1)
                        && (element.properties.data.typeId !== "START_BLOCKCHAIN_EVENT_PLUGIN" || element.outEdges.length < 1)
                        && (element.properties.data.typeId !== "EXTERNAL_SOURCE_PLUGIN" || element.outEdges.length < 1)
                        && (element.properties.data.typeId !== "ADVANCED_LOGIC_PLUGIN" || element.outEdges.length < 1)
                        && (element.properties.data.typeId !== "BLOCKCHAIN_PLUGIN" || element.outEdges.length < 1)
                        && checkForDynamicPlugin()
                    ) {
                        diagramInstance.selectedItems.userHandles[0].visible = true;
                    }
                    // /////////
                    element.style.strokeColor = "#3E71D2"; // highlight
                    const { id: PLUGIN_UUID, data: { typeId: PLUGIN_ID } } = element;
                    this.PLUGIN_SELECTED({ PLUGIN_ID, PLUGIN_UUID });
                    diagramInstance.bringToFront();
                } else if (element.propName === "connectors") {
                    // selection part
                    diagramInstance.selectedItems.constraints = SelectorConstraints.ResizeSouthWest
                    | SelectorConstraints.ConnectorTargetThumb
                    | SelectorConstraints.ConnectorSourceThumb;
                    const { sourceID: FROM_UUID, id: CONNECTOR_UUID } = element;
                    highlightConnectors = [CONNECTOR_UUID];
                    let FROM_ID = this.diagramData.content.find(({ uuid }) => FROM_UUID === uuid);
                    if (FROM_ID) {
                        FROM_ID = FROM_ID.id; // "id" is actually the node TYPE (GENERAL_START, TASK...., etc)
                        this.CONNECTOR_SELECTED({
                            CONNECTOR_UUID,
                            FROM_UUID,
                            FROM_ID,
                            CUSTOM_EVENT_CONN_SELECTED: this.customEventConnSelected
                        });
                    } else {
                        this.PLUGIN_SELECTED({});
                    }
                    // ///////////
                }
            } else {
                // highlight nodes on multiple selection
                diagramInstance.selectedItems.nodes.forEach(node => {
                    node.style.strokeColor = "#3E71D2";
                });
                this.PLUGIN_SELECTED({}); // unselect from right panel
            }

            // set connectors color
            diagramInstance.connectors.forEach(conn => {
                if (highlightConnectors.includes(conn.id)) {
                    conn.style.strokeColor = "#3E71D2";
                    conn.targetDecorator.style.strokeColor = "#3E71D2";
                    conn.targetDecorator.style.fill = "#3E71D2";
                } else {
                    conn.style.strokeColor = "#6C778B";
                    conn.targetDecorator.style.strokeColor = "#6C778B";
                    conn.targetDecorator.style.fill = "#6C778B";
                }
            });
            diagramInstance.dataBind();
            // ////////////
        },
        addNodeToState(node) {
            const PLUGIN_UUID = node.id;
            const PLUGIN_ID = node.data.typeId;
            const newNode = {
                uuid: PLUGIN_UUID,
                id: PLUGIN_ID,
                x: node.offsetX,
                y: node.offsetY,
                width: node.width,
                height: node.height,
                label: {
                    value: node.annotations[0].content,
                    uuid: node.annotations[0].id,
                    x: node.annotations[0].offset.x,
                    y: node.annotations[0].offset.y,
                    labelOf: PLUGIN_UUID,
                    pluginUUID: PLUGIN_UUID,
                    pluginID: PLUGIN_ID
                },
                anchoredPlugins: [],
                outgoing: [],
                incoming: []
            };
            const data = {
                ...this.diagramData,
                content: [...this.diagramData.content, newNode]
            };
            this.$store.commit("diagrams/diagramData", data);
        },
        deleteNodeFromState(PLUGIN_UUID) {
            this.diagramData.content = this.diagramData.content.filter(node => node.uuid !== PLUGIN_UUID);
        },
        addConnectorToState(CONNECTOR_UUID, FROM_UUID, TO_UUID) {
            const newConnector = {
                uuid: CONNECTOR_UUID,
                from: FROM_UUID,
                to: TO_UUID,
                endSide: "",
                connectorCode: "",
                path: [],
                label: {}
            };
            this.diagramData.connectors = [...this.diagramData.connectors, newConnector];
        },
        deleteConnectorFromState(CONNECTOR_UUID, FROM_UUID, TO_UUID) {
            this.diagramData.connectors = this.diagramData.connectors.filter(({ uuid }) => CONNECTOR_UUID !== uuid);
            this.diagramData.content.forEach(node => {
                if (node.uuid === FROM_UUID) {
                    node.outgoing = node.outgoing.filter(el => el !== CONNECTOR_UUID);
                } else if (node.uuid === TO_UUID) {
                    node.incoming = node.incoming.filter(el => el !== CONNECTOR_UUID);
                }
            });
        },
        collectionChange(ev) {
            const { element, state, type } = ev;
            if (state !== "Changed") {
                return;
            }
            let data;
            if (element.propName === "nodes") {
                const PLUGIN_UUID = element.id;
                const PLUGIN_ID = element.data.typeId;
                const PLUGIN_LABEL = element.annotations[0].content;
                if (type === "Addition") {
                    this.$refs.canvasElements.$emit("changeSelectedTab", "details");
                    data = {
                        payload: {
                            PLUGIN_ID,
                            PLUGIN_UUID,
                            PLUGIN_LABEL
                        },
                        type: "PLUGIN_CREATED"
                    };
                    this.addNodeToState(element);
                } else if (type === "Removal") {
                    data = {
                        payload: {
                            PLUGIN_ID,
                            PLUGIN_UUID
                        },
                        type: "PLUGIN_DELETED"
                    };
                    this.deleteNodeFromState(PLUGIN_UUID, PLUGIN_ID);
                }
            } else if (element.propName === "connectors") {
                const { id: CONNECTOR_UUID, sourceID: FROM_UUID, targetID: TO_UUID } = element;
                if (type === "Addition") {
                    data = {
                        payload: {
                            CONNECTOR_UUID,
                            FROM_UUID,
                            TO_UUID,
                            CONNECTOR_LABEL: "",
                            FROM_ID: "",
                            TO_ID: ""
                        },
                        type: "CONNECTOR_CREATED"
                    };
                    this.addConnectorToState(CONNECTOR_UUID, FROM_UUID, TO_UUID);
                } else if (type === "Removal") {
                    data = {
                        payload: {
                            CONNECTOR_UUID,
                            FROM_UUID,
                            TO_UUID,
                            FROM_ID: "",
                            TO_ID: ""
                        },
                        type: "CONNECTOR_DELETED"
                    };
                    this.deleteConnectorFromState(CONNECTOR_UUID, FROM_UUID, TO_UUID);
                }
            }
            this.onDiagramEvent(data);
        },
        keyDown(ev) {
            if (ev.target.id === "diagramcontent") {
                ev.preventDefault();
                if (diagramInstance.tool !== DiagramTools.MultipleSelect) {
                    diagramInstance.tool = DiagramTools.MultipleSelect;
                }
            }
        },
        keyUp(ev) {
            if (ev.target.id === "diagramcontent") {
                ev.preventDefault();
                if (diagramInstance.tool !== DiagramTools.SingleSelect | DiagramTools.ZoomPan) {
                    diagramInstance.tool = DiagramTools.SingleSelect | DiagramTools.ZoomPan;
                }
            }
        },
        centerDiagram() {
            setTimeout(() => {
                const coords = diagramInstance.diagramLayer.getBoundingClientRect();
                const area = new Rect(coords.x, coords.y, coords.width, coords.height);
                diagramInstance.bringToCenter(area);
            });
        },
        zoom(inn) {
            if (inn === "reset") {
                diagramInstance.reset();
                this.centerDiagram();
            } else if (inn) {
                diagramInstance.zoom(1.1);
            } else {
                diagramInstance.zoom(0.9);
            }
        },
        setReadOnly(readOnly) {
            if (readOnly) {
                diagramInstance.tool = DiagramTools.ZoomPan;
                diagramInstance.scrollSettings.canAutoScroll = false;
            } else {
                diagramInstance.tool = DiagramTools.SingleSelect | DiagramTools.ZoomPan;
                diagramInstance.scrollSettings.canAutoScroll = true;
            }
        },
        loadDiagram(data, hideCheckSaveBtn) {
            diagramInstance.loadDiagram(data);
            // eslint-disable-next-line no-lone-blocks
            { // delete this block when ONE NODE BUG is fixed
                diagramInstance.dataBind();
                setTimeout(() => {
                    const TBD = diagramInstance.nodes.filter(node => node.id.slice(0, 11) === "ToBeDeleted");
                    if (TBD.length === diagramInstance.nodes.length) {
                        diagramInstance.clear();
                    } else {
                        TBD.forEach(element => {
                            diagramInstance.remove(element);
                        });
                    }
                    diagramInstance.clearHistory();
                    this.$root.$emit("showCheckedSaveBtn", hideCheckSaveBtn ? !hideCheckSaveBtn : true);
                }, 10);
            }
            diagramInstance.reset();
            this.centerDiagram();
        },
        getTool(action) {
            let tool;
            if (action === "drawConnector") {
                const currNode = diagramInstance.selectedItems.nodes[0];
                const actionTool = new ConnectorDraw(diagramInstance.commandHandler, "", currNode);
                actionTool.diagram = diagramInstance;
                actionTool.node = currNode;
                actionTool.vueInstance = this;
                return actionTool;
            }
            return tool;
        },

        scrollChange(args) {
            this.zoomLevel = Math.round(args.newValue.CurrentZoom * 100);
        },
        publishAppPermissions,
        getDiagramWarnings(data) {
            this.diagramWarnings = data || [];
            if (this.pluginDescriptionCard) {
                this.$nextTick(() => {
                    eventBus.$emit("resetMaxHeight", this.$refs.errorPanelRef);
                });
            }
        },
        getDiagramErrors(data) {
            this.diagramErrors = data || [];
            if (this.pluginDescriptionCard) {
                this.$nextTick(() => {
                    eventBus.$emit("resetMaxHeight", this.$refs.errorPanelRef);
                });
            }
        },
        openCurrentConnector(conn) {
            const selectedConn = diagramInstance.connectors.find(elem => elem.id === conn.id);
            if (!this.readonly && selectedConn) {
                this.customEventConnSelected = false;
                diagramInstance.select([selectedConn]);
            }
        },
        openCurrentPlugin(plugin) {
            const { type, id } = plugin;
            const data = {
                id: type,
                plvType: false,
                uuid: id
            };
            this.selectPlugin(data);
        },
        afterShow() {
            eventBus.$emit("resetMaxHeight", this.$refs.errorPanelRef);
        },

        updateLabel(v) {
            const stateNode = this.diagramData.content.find(({ uuid }) => uuid === v.uuid);
            const node = diagramInstance.nodes.find(({ id }) => id === v.uuid);
            if (stateNode && node) {
                stateNode.label.value = v.value;
                node.annotations[0].content = v.value;
                node.annotations[0].visibility = this.diagramSettings.showLabels;
            }
            this.$store.commit("diagrams/setConfigData", v);
        },
        getBlockchainData(param) {
            const obj = {
                param,
                responseNull: true
            };
            this.getScDetails(obj);
        },
        selectNone(ev) {
            if (ev.target.id === "diagram_diagramLayer_svg" && diagramInstance.tool !== DiagramTools.MultipleSelect) {
                diagramInstance.clearSelection();
            }
        },
        /// selects the node in the right panel
        selectPlugin(plugin) {
            if (plugin && plugin.uuid) {
                this.$store.commit("diagrams/selectedPluginUuid", plugin.uuid);
                this.$store.commit("diagrams/selectedPluginCode", plugin.id);
                const target = diagramInstance.nodes.find(({ id }) => id === plugin.uuid);
                if (!this.readonly && target) {
                    diagramInstance.select([target]);
                }
            }
        },
        cleanExpirationRules(connectorsFrom) {
            connectorsFrom.forEach(connector => {
                const activityToDelete = this.diagramConfigData.content.find(activity => connector === activity.uuid && activity.expiration);
                if (activityToDelete) {
                    delete activityToDelete.expiration;
                }
            });
        },
        cleanExclusiveGatewayRules(connectorFrom, connectorUuid) {
            const { content } = this.diagramConfigData;
            const PLUGIN = "EXCLUSIVE_GATEWAY_PLUGIN";
            const activity = content.find(
                ({ uuid, pluginType, exclusiveGatewayRoutes }) => exclusiveGatewayRoutes && uuid === connectorFrom && pluginType === PLUGIN
            );
            if (activity) {
                const gwRuleIndex = activity.exclusiveGatewayRoutes.findIndex(({ connectorId }) => connectorId === connectorUuid);
                if (gwRuleIndex !== -1) {
                    activity.exclusiveGatewayRoutes.splice(gwRuleIndex, 1);
                }
            }
        },
        DIAGRAM_VIEW_INITIALIZED() {
            this.setDisableOnLoad(true);

            this.$store.commit("diagrams/selectedPluginCode", null);
            this.$store.commit("diagrams/selectedPluginUuid", null);
            this.$store.commit("diagrams/configCache", {
                connectors: {},
                content: {}
            });
            /// /// get diagram DATA //////////////////////
            this.getSettings().finally(() => {
                this.$store.dispatch("diagrams/getDataJson").then(() => {
                    this.checkGeneralStartRecurrence();
                    if (this.diagramData.connectors.length > 299) {
                        this.applyBridges(false, true);
                        this.notifyUpdate = this.$toast.showNotify({
                            type: "info",
                            message: this.$t("diagrams.performanceInfo")
                        });
                    }
                    this.loadDiagram(convertOldDiagram(this.diagramData, this.diagramSettings));
                    this.processPreview = JSON.parse(JSON.stringify(this.diagramData));
                    this.processConfigPreview = JSON.parse(JSON.stringify(this.diagramConfigData));
                    this.setReadOnlyFalse();
                    this.resetSaveHistory();
                    this.dirtyFromRestore = false;
                    this.$emit("previewSaveHistoryClose", this.$route.name);
                    this.$root.$emit("previewSaveHistoryHeaderClose", this.$route.name);
                    this.$root.$emit("showCheckedSaveBtn", true);
                    this.checkPermissions();
                    this.setDisableOnLoad(false);
                }).finally(() => {
                    this.resetCanlogDiagramFunction();
                });
            });
        },
        PLUGIN_SELECTED(data) {
            if (data.PLUGIN_ID) {
                this.$store.commit("diagrams/selectedPluginCode", data.PLUGIN_ID);
                this.$store.commit("diagrams/selectedPluginUuid", data.PLUGIN_UUID);
                this.$refs.canvasElements.$emit("changeSelectedTab", "details");
                this.$store.commit("diagrams/setLastPluginCreated", data);
                this.openCollapsible = true;
            } else if (!this.isConnSelectWarning) {
                this.$store.commit("diagrams/selectedPluginUuid", null);
            }
        },
        CONNECTOR_SELECTED(data) {
            const vm = this;
            this.customEventConnSelected = true;
            if (data.FROM_UUID) {
                vm.$store.commit("diagrams/selectedPluginCode", data.FROM_ID);
                vm.$store.commit("diagrams/selectedPluginUuid", data.FROM_UUID);
                if (this.selectedConnUuid === data.CONNECTOR_UUID) {
                    this.isConnSelectWarning = true;
                }

                vm.$nextTick(() => {
                    vm.$refs.canvasElements.$emit("changeSelectedTab", "targets");

                    // need to wait for targets tab to be loaded
                    let selector = null;
                    let interval = null;
                    interval = setInterval(() => {
                        if (document.getElementById("targetsList")) {
                            clearInterval(interval);
                            selector = document.getElementById("targetsList");
                            const element = selector.querySelector(`label[data-uid="${data.CONNECTOR_UUID}"]`);
                            if (element) {
                                if (data.CUSTOM_EVENT_CONN_SELECTED) {
                                    element.classList.add("set-active");
                                } else {
                                    element.click();
                                }
                            }
                            this.isConnSelectWarning = false;
                            this.selectedConnUuid = null;
                        }
                    }, 30);
                });
            }
        },
        PLUGIN_CREATED(data) {
            const {
                payload: { PLUGIN_LABEL, PLUGIN_ID, PLUGIN_UUID }
            } = data;
            this.$store.commit("diagrams/setLastPluginCreated", data);
            const fromCache = JSON.parse(JSON.stringify(this.configCache.content[PLUGIN_UUID] || {}));

            this.$store.commit("diagrams/addNewConfigObject", {
                type: "content",
                payload: {
                    ...{
                        uuid: PLUGIN_UUID,
                        label: PLUGIN_LABEL,
                        pluginType: PLUGIN_ID
                    },
                    ...fromCache
                }
            });

            // clear cache
            delete this.configCache.content[data.payload.PLUGIN_UUID];
        },
        CONNECTOR_CREATED(data) {
            const vm = this;
            const fromCache = JSON.parse(JSON.stringify(this.configCache.content[data.payload.PLUGIN_UUID] || {}));
            vm.$store.commit("diagrams/addNewConfigObject", {
                type: "connector",
                payload: {
                    ...{
                        uuid: data.payload.CONNECTOR_UUID,
                        label: data.payload.CONNECTOR_LABEL,
                        from: data.payload.FROM_UUID,
                        to: data.payload.TO_UUID
                    },
                    ...fromCache
                }
            });
            // clear cache
            delete this.configCache.connectors[data.payload.PLUGIN_UUID];
            this.$root.$emit("connectorAddedDeleted");
        },
        PLUGIN_LABEL_UPDATED(data) {
            const vm = this;
            vm.$store.commit("diagrams/setConfigData", {
                uuid: data.payload.PLUGIN_UUID,
                value: data.payload.LABEL_VALUE,
                property: "label",
                type: "content"
            });
        },
        CONNECTOR_LABEL_UPDATED(data) {
            const vm = this;
            vm.$store.commit("diagrams/checkConnectorsUUIDs", data);
            vm.$store.commit("diagrams/setConfigData", {
                uuid: data.payload.CONNECTOR_UUID,
                value: data.payload.LABEL_VALUE,
                property: "label",
                type: "connector"
            });
        },
        PLUGIN_DELETED(data) {
            const vm = this;
            const uuid = data.payload.PLUGIN_UUID;
            const connectorsFrom = [];
            // get all connectors that connect this plugin
            const connectors = vm.diagramConfigData.connectors.filter(el => el.to === uuid);

            // parse all connectors
            connectors.forEach(connector => {
                connectorsFrom.push(connector.from);
                this.cleanExclusiveGatewayRules(connector.from, connector.uuid);
            });
            if (connectorsFrom.length) {
                this.cleanExpirationRules(connectorsFrom);
            }
            // add it to cache before deletion
            this.$store.commit("diagrams/addConfigToCache", {
                type: "content",
                uuid
            });

            vm.diagramConfigData.content = vm.diagramConfigData.content.filter(el => el.uuid !== uuid);
        },
        CONNECTOR_DELETED(data) {
            const uuid = data.payload.CONNECTOR_UUID;

            this.cleanExclusiveGatewayRules(data.payload.FROM_UUID, data.payload.CONNECTOR_UUID);

            // add it to cache before deletion
            this.$store.commit("diagrams/addConfigToCache", {
                type: "connectors",
                uuid
            });

            this.diagramConfigData.connectors = this.diagramConfigData.connectors.filter(el => el.uuid !== uuid);
            this.$root.$emit("connectorAddedDeleted");
        },
        onDiagramEvent(data) {
            const vm = this;
            if (data.payload.FROM_ID !== "" && data.payload.TO_ID !== "") {
                vm.$root.$emit("showCheckedSaveBtn", false);
                vm.$store.commit("main/setInterfaceDirty", true);
            }
            if (data.type === "MULTIPLE_ACTIONS_HAPPENED") {
                data.payload.forEach(el => {
                    if (typeof vm[el.event] === "function") {
                        vm[el.event]({ payload: el.payload });
                    }
                });
            } else if (typeof vm[data.type] === "function") {
                vm[data.type](data);
            }
        },
        save(val) {
            const vm = this;
            sessionStorage.setItem("wsActionUser", true);
            const revertedData = revertToOldDiagram({
                content: diagramInstance.nodes,
                connectors: diagramInstance.connectors
            });
            return $http(
                {
                    url: `/design/process/diagram/persist?processDefinitionId=${vm.getCurrentProcessId}&applicationId=${vm.getCurrentApplicationId}`,
                    type: "PUT",
                    data: {
                        ...vm.diagramConfigData,
                        diagram: JSON.stringify(revertedData)
                    }
                },
                true
            )
                .then(data => {
                    this.$store.commit("diagrams/setCopyOfInitialConnectorsDiagram", JSON.stringify(this.diagramConfigData.connectors));
                    this.$store.commit("diagrams/setCopyOfInitialMessagesDiagam", JSON.stringify(this.diagramConfigData.messages));
                    this.$store.commit("diagrams/setCopyOfInitialContentDiagram", JSON.stringify(this.diagramConfigData.content));
                    this.processPreview = JSON.parse(JSON.stringify(this.diagramData));
                    this.processConfigPreview = JSON.parse(JSON.stringify(this.diagramConfigData));
                    this.dirtyFromRestore = false;
                    this.$store.commit("apps/updateCurrentAppStatus", data);
                    this.$store.commit("diagrams/setDiagramStateModifications");
                    const dataWarnings = data.data.warnings && data.data.warnings.length ? data.data.warnings : null;
                    const warnings = [];

                    if (dataWarnings) {
                        dataWarnings.forEach(element => {
                            const tooltipNewMesage = element.message.split(element.id);
                            if (element.type === "CONNECTOR") {
                                revertedData.connectors.forEach(el => {
                                    if (el.uuid === element.id) {
                                        element.label = el.label.value
                                            ? `"${el.label.value}" ${this.$t("diagrams.connectorType")}`
                                            : this.$t("diagrams.untitledConnector");
                                    }
                                });
                                element.tooltip = this.$t("diagrams.connectorType")
                                + (tooltipNewMesage.length ? tooltipNewMesage[tooltipNewMesage.length - 1] : "");
                            } else {
                                element.label = "";
                                if (revertedData && revertedData.content) {
                                    const warningPluginLabel = revertedData.content.find(elem => elem.uuid === element.id);
                                    if (warningPluginLabel) {
                                        element.label = warningPluginLabel.label.value || "";
                                    }
                                }
                                element.tooltip = element.label && tooltipNewMesage.length ? element.label + tooltipNewMesage[tooltipNewMesage.length - 1] : "";
                            }
                            warnings.push(element);
                        });
                    }
                    this.$store.commit("diagrams/setWarningsList", warnings);
                    this.currentApp.publishStatus = data.data.status ? "UNPUBLISHED_CHANGES" : "PUBLISHED";
                    this.$store.commit("apps/setCurrentApp", this.currentApp);
                    if (val) {
                        this.$root.$emit("savedSuccessfully");
                    }
                    vm.$toast.showNotify({
                        type: "success",
                        message: this.$t("generic.savedSuccessfully")
                    });
                })
                .catch(e => {
                    sessionStorage.removeItem("wsActionUser");
                    vm.$toast.showNotify({
                        type: "error",
                        message: e.response.data.error
                    });
                });
        },
        checkGeneralStartRecurrence() {
            if (this.diagramConfigData && this.diagramConfigData.content) {
                this.diagramConfigData.content.forEach(element => {
                    if (element.pluginType === "GENERAL_START_PLUGIN") {
                        if (element.recurrence && element.recurrence.endDate) {
                            const newDate = new Date();
                            const recurrenceDate = new Date(element.recurrence.endDate);
                            const diff = date.getDateDiff(recurrenceDate, newDate, "days");
                            if (diff < 0) {
                                for (let i = 0; i < this.diagramData.content.length; i++) {
                                    const plugin = this.diagramData.content[i];
                                    if (plugin.uuid === element.uuid && plugin.label.additionalOptions) {
                                        plugin.label.additionalOptions = { ...plugin.label.additionalOptions, recurrenceExpired: true };
                                        break;
                                    }
                                }
                            }
                        }
                    }
                    const index = this.diagramData.content.findIndex(item => item.uuid === element.uuid && item.label.value !== element.label);
                    if (index !== -1) {
                        this.diagramData.content[index].label.value = element.label;
                    }
                });
            }
        },
        checkPermissions() {
            const { params } = this.$route;
            const isReadonly = this.readonlyPermission || params.isMarkedForArchiving;
            if (isReadonly) {
                this.readonly = true;
                this.$store.commit("main/setSaveBtnVisible", this.readonly);
                this.$root.$emit("setReadonlyTag", this.readonly);
            } else if (this.oneAtATimeParams.hasOwnProperty("readonly")) {
                this.readonly = !this.$oneAtATime.state.$currentRoomEmpty;
                this.$store.commit("main/setSaveBtnVisible", this.readonly);
                this.$root.$emit("setReadonlyTag", this.readonly);
            } else {
                this.readonly = false;
                this.$store.commit("main/setSaveBtnVisible", this.readonly);
                this.$root.$emit("setReadonlyTag", this.readonly);
            }
            this.setReadOnly(this.readonly);
        },
        refreshPage() {
            window.location.reload();
        },
        crumbOpen(guid) {
            const entity = this.processList.find(({ id }) => id === guid);
            if (entity) {
                this.$store.commit("apps/setCurrentProcess", entity);
                this.$store.commit("main/setCurrentProcessId", entity.id);
                this.$store.commit("diagrams/setCopyOfInitialContentDiagram", []);
                this.$store.commit("diagrams/setCopyOfInitialConnectorsDiagram", []);
                this.$store.commit("diagrams/setCopyOfInitialMessagesDiagam", []);
                this.$router.push(`/applications/${this.currentApp.id}/processes/${entity.id}`);
                this.showHeaderBtnsAfterPreview();
            }
            if (!this.readonlyPermission && !this.$route.isMarkedForArchiving) {
                this.$oneAtATime.createRoom("process", entity.id);
            }
        },
        saveSettings() {
            this.settingsEnabled = false;
            if (this.readonly) {
                return new Promise((resolve) => { resolve(); });
            }
            return $http(
                {
                    url: `/design/process/${this.getCurrentProcessId}/diagram/settings`,
                    type: "PUT",
                    data: {
                        ...this.diagramSettings
                    }
                },
                true
            );
        },
        getSettings() {
            this.settingsEnabled = false;
            const processToLoad = this.$route.params && this.$route.params.processId ? this.$route.params.processId : this.getCurrentProcessId;
            return $http(
                {
                    url: `/design/process/${processToLoad}/diagram/settings`,
                    type: "GET"
                },
                true
            ).then((data) => {
                const { showGrid = true, showBridges = true, showLabels = true } = data.data;
                if (showGrid !== this.diagramSettings.showGrid) {
                    this.applyGrid(showGrid, false);
                }
                if (showBridges !== this.diagramSettings.showBridges) {
                    this.applyBridges(showBridges, false);
                }
                if (showLabels !== this.diagramSettings.showLabels) {
                    this.applyAnnotations(showLabels, false);
                }
            });
        },
        applyGrid(value, save) {
            if (this.diagramSettings.showGrid !== value) {
                this.diagramSettings.showGrid = value;
                if (save) {
                    this.saveSettings().finally(() => {
                        setTimeout(() => {
                            this.settingsEnabled = true;
                        }, 500);
                    });
                }
            }
            if (value && diagramInstance.snapSettings.constraints !== 15) {
                diagramInstance.snapSettings.constraints = 15; // SnapConstraints.ShowLines | SnapConstraints.SnapToLines,
            } else if (!value && diagramInstance.snapSettings.constraints !== 12) {
                diagramInstance.snapSettings.constraints = 12; // SnapConstraints.SnapToLines,
            }
        },
        applyBridges(value, save) {
            if (this.diagramSettings.showBridges !== value) {
                this.diagramSettings.showBridges = value;
                if (save) {
                    this.saveSettings().finally(() => {
                        setTimeout(() => {
                            this.settingsEnabled = true;
                        }, 500);
                    });
                }
            }

            if (value) {
                diagramInstance.connectors.forEach(conn => {
                    conn.constraints = 470902;
                });
            } else {
                diagramInstance.connectors.forEach(conn => {
                    conn.constraints = 470646;
                });
            }
        },
        applyAnnotations(value, save) {
            if (this.diagramSettings.showLabels !== value) {
                this.diagramSettings.showLabels = value;
                if (save) {
                    this.saveSettings().finally(() => {
                        setTimeout(() => {
                            this.settingsEnabled = true;
                        }, 500);
                    });
                }
                annotationConfig.constraints = value ? AnnotationConstraints.None : AnnotationConstraints.ReadOnly;
                diagramInstance.connectors.forEach(conn => {
                    conn.annotations.forEach(ann => {
                        ann.style.fill = ann.content ? "#F3F5F7" : "none";
                        ann.visibility = value;
                        ann.constraints = value ? AnnotationConstraints.None : AnnotationConstraints.ReadOnly;
                    });
                });
                diagramInstance.nodes.forEach(node => {
                    node.annotations.forEach(ann => {
                        ann.visibility = value;
                        ann.constraints = value ? AnnotationConstraints.None : AnnotationConstraints.ReadOnly;
                    });
                });
                diagramInstance.dataBind();
            }
        },
        renameSelectedProcess({ newName }) {
            let processNameWrongPattern, processDuplicatedName, toastMsg;
            const processName = newName.trim();
            const showNotif = (msg, type = "") => {
                this.$toast.showNotify({
                    message: this.$t(`processes.${msg}`),
                    type
                });
            };
            if (!processName) {
                showNotif("emptyProcessNameNotif", "error");
            } else if (processName.length >= 3 && processName !== this.currentProcess.name) {
                const validatePattern = new RegExp("^[a-zA-Z][ a-zA-Z0-9_-]*$"); // alphanumeric chars and space - _
                processNameWrongPattern = !validatePattern.test(processName);
                processDuplicatedName = this.processList.some(elem => elem.name === processName);

                if (processNameWrongPattern) {
                    toastMsg = "wrongPatternErr";
                } else if (processDuplicatedName) {
                    toastMsg = "duplicatedProcessName";
                }
                if (toastMsg) {
                    showNotif(toastMsg, "error");
                } else {
                    const payload = {
                        appId: this.currentApp.id,
                        id: this.currentProcess.id,
                        name: processName
                    };
                    this.renameProcess(payload).then(
                        (data) => {
                            this.currentProcess.name = processName;
                            this.currentApp.publishStatus = data.data.status ? "UNPUBLISHED_CHANGES" : "PUBLISHED";
                            this.processList.forEach(process => {
                                if (process.id === this.currentProcess.id) {
                                    process.name = processName;
                                }
                            });
                            this.processRenamed = true;
                            showNotif("successfullyRenamed", "success");
                        }
                    )
                        .catch(() => {
                            showNotif("failedRename", "error");
                        });
                }
            }
        },
        /// ///
        historyMenuClick(evt) {
            this.dialogAction = evt.actionName;
            if (this.dialogAction === "restore") {
                this.infoRestoreDialog(evt.timelineItem);
                this.restoreConfirmationDialog = true;
            }
        },
        infoRestoreDialog(timelineItem) {
            this.restoredProcessData = timelineItem || this.currentPreviewProcess;
            const restoreElementData = timelineItem && timelineItem.timestamp ? `${this.dateFormatter(timelineItem.timestamp)}`
                : `${this.dateFormatter(this.currentPreviewProcess.timestamp)}`;
            this.restoreDialogMessage = this.$t("versioning.restoreDialogInfo", { interfaceName: this.currentProcess.name, date: restoreElementData });
        },
        confirmRestore() {
            if ((this.previewDone && this.restoredProcessData && this.restoredProcessData !== this.currentPreviewProcess) || !this.previewDone) {
                const payload = {
                    module: "UIBuilder",
                    applicationId: this.currentApp.id,
                    elementId: this.getCurrentProcessId,
                    commitId: this.restoredProcessData.commitId
                };
                this.getProcessHistoryPreviewData(payload).then(data => {
                    if (data && data.data) {
                        const dataFromRestore = data.data;
                        if (!dataFromRestore.content) {
                            dataFromRestore.content = [];
                        }
                        if (!dataFromRestore.connectors) {
                            dataFromRestore.connectors = [];
                        }
                        if (!dataFromRestore.messages) {
                            dataFromRestore.messages = [];
                        }
                        this.$root.$emit("hideSaveBtn", false);
                        this.$root.$emit("showCheckedSaveBtn", false);
                        this.$root.$emit("confirmRestore", this.$route.name);
                        if (dataFromRestore.diagram) {
                            this.$store.commit("diagrams/diagramConfigData", dataFromRestore);
                            this.$store.commit("diagrams/setCopyOfInitialConnectorsDiagram", JSON.stringify(dataFromRestore.connectors));
                            this.$store.commit("diagrams/setCopyOfInitialMessagesDiagam", JSON.stringify(dataFromRestore.messages));
                            this.$store.commit("diagrams/setCopyOfInitialContentDiagram", JSON.stringify(dataFromRestore.content));
                            this.$store.commit("diagrams/diagramData", JSON.parse(dataFromRestore.diagram));
                            this.loadDiagram(convertOldDiagram(this.diagramData, this.diagramSettings), true);
                        } else {
                            this.resetDiagramStoreData();
                            this.loadDiagram(convertOldDiagram(this.diagramData, this.diagramSettings), true);
                        }
                        this.$toast.showNotify({
                            type: "success",
                            message: this.$t("versioning.confirmRestoreNotify"),
                            timeout: 2000
                        });
                    }
                }).finally(() => {
                    this.resetCanlogDiagramFunction();
                });
            }
            this.$root.$emit("showPublish", { noProcesses: this.currentApp.hasOwnProperty("nrOfProcesses") ? !this.currentApp.nrOfProcesses
                : !this.processList.length,
            showPublish: publishAppPermissions(this.currentApp, this.getCurrentAppUserPermissions) });
            this.saveHistoryActiveCommitId = null;
            this.resetSaveHistory();
            this.dirtyFromRestore = true;
            this.inPreviewMode = false;
            this.$store.commit("main/setInterfaceDirty", true);
            this.saveHistoryModal = false;
            this.setReadOnlyFalse();
            this.$root.$emit("hideSaveBtn", false);
            this.$root.$emit("showCheckedSaveBtn", false);
            this.$root.$emit("confirmRestore", this.$route.name);
        },
        getNoteData() {
            if (this.currentPreviewProcess) {
                const payload = {
                    module: "ProcessDiagram",
                    applicationId: this.currentApp.id,
                    elementId: this.getCurrentProcessId,
                    commitId: this.currentPreviewProcess.commitId
                };
                this.getNote(payload).then(data => {
                    if (data) {
                        this.note = data.data;
                        this.createNote = !this.note;
                        this.confirmLabel = this.note ? this.$t("versioning.updateNote") : this.$t("versioning.createNote");
                        this.noteDialogTitle = this.note
                            ? this.$t("versioning.dialogTitleEdit", { date: `${this.dateFormatter(this.currentPreviewProcess.timestamp)}` })
                            : this.$t("versioning.dialogTitleCreate", { date: `${this.dateFormatter(this.currentPreviewProcess.timestamp)}` });
                    }
                    this.noteDialog = true;
                });
            }
        },
        actionNote(noteData) {
            const payload = {
                module: "ProcessDiagram",
                applicationId: this.currentApp.id,
                elementId: this.getCurrentProcessId
            };
            payload.commitId = noteData ? noteData.timelineItem.commitId : this.currentPreviewProcess.commitId;
            payload.note = noteData ? noteData.note : this.note;
            let messageLabel = "";
            if (noteData) {
                messageLabel = this.dialogAction === "edit" ? this.$t("versioning.updateNoteDialog") : this.$t("versioning.saveNote");
            } else {
                messageLabel = this.confirmLabel === "Update" ? this.$t("versioning.updateNoteDialog") : this.$t("versioning.saveNote");
            }
            this.saveDeleteNote(payload).then((data) => {
                if (data) {
                    this.$toast.showNotify({
                        type: "success",
                        message: messageLabel,
                        timeout: 500,
                        hasClose: true
                    });
                    if (noteData) {
                        noteData.timelineItem.note = noteData.note;
                        this.keyUpdate += 1;
                    } else if (this.currentPreviewProcess) {
                        this.currentPreviewProcess.note = this.note;
                    }
                }
            });
        },
        modalActionSaveHistory(evt) {
            if (evt === "close") {
                this.saveHistoryModal = false;
                if (this.inPreviewMode) {
                    this.savePreviewToast = this.$toast.showNotify({
                        type: "info",
                        message: `${this.$t("versioning.previewToastTitle")} ${this.dateFormatter(this.timeStampPreview)}`,
                        caption: this.$t("versioning.previewToastText"),
                        code: "preview-ui",
                        timeout: 30000000
                    });
                }
            }
        },
        getSaveHistory() {
            if (this.saveHistoryHasMoreItems) {
                const payload = {
                    searchType: "SAVE_HISTORY",
                    module: "ProcessDiagram",
                    applicationId: this.currentApp.id,
                    elementId: this.getCurrentProcessId,
                    page: this.saveHistoryCurrentPage,
                    size: 10
                };
                this.getHistoryData(payload).then(data => {
                    if (data && data.data && data.data.history) {
                        this.saveHistoryData = this.saveHistoryData.concat(data.data.history);
                        this.saveHistoryHasMoreItems = data.data.hasMorePages;
                    }
                    this.saveHistoryModal = true;
                });
            } else {
                this.saveHistoryModal = true;
            }
        },
        resetSaveHistory() {
            this.saveHistoryData = [];
            this.saveHistoryCurrentPage = 0;
            this.saveHistoryHasMoreItems = true;
            this.saveHistoryActiveCommitId = null;
            if (this.savePreviewToast) {
                this.savePreviewToast();
            }
        },
        getSaveHistoryPreview(timelineItem) {
            const isSamePreview = this.saveHistoryActiveCommitId ? this.saveHistoryActiveCommitId === timelineItem.commitId : false;
            if (!this.standaloneTypes.has(timelineItem.commitType) && !isSamePreview) {
                this.currentPreviewProcess = timelineItem;
                const dirtyNow = this.interfaceDirty || this.dirtyFromRestore;
                if (dirtyNow && !this.inPreviewMode) {
                    this.openDialogDraftPreview = dirtyNow;
                } else {
                    this.getSaveHistoryItemPreview();
                }
            }
        },
        getSaveHistoryItemPreview() {
            if (!this.standaloneTypes.has(this.currentPreviewProcess.commitType)) {
                this.$root.$emit("previewSaveHistoryLoadStart", this.$route.name);
                const payload = {
                    module: "ProcessDiagram",
                    applicationId: this.currentApp.id,
                    elementId: this.getCurrentProcessId,
                    commitId: this.currentPreviewProcess.commitId
                };
                this.getProcessHistoryPreviewData(payload).then(data => {
                    if (data && data.data) {
                        const previewData = data.data;
                        if (!previewData.content) {
                            previewData.content = [];
                        }
                        if (!previewData.connectors) {
                            previewData.connectors = [];
                        }
                        if (!previewData.messages) {
                            previewData.messages = [];
                        }
                        this.$root.$emit("previewSaveHistoryLoadEnd", this.$route.name);
                        this.$root.$emit("hideSaveBtn", true);
                        this.$root.$emit("changePublishError");
                        this.$root.$emit("showPublish", false);

                        this.timeStampPreview = previewData.timestamp;

                        this.previewDone = true;
                        this.inPreviewMode = true;
                        this.dirtyFromRestore = false;
                        this.$store.commit("main/setInterfaceDirty", false);
                        this.$store.commit("diagrams/selectedPluginUuid", null);
                        this.saveHistoryActiveCommitId = previewData.commitId;
                        if (previewData.diagram) {
                            this.$store.commit("diagrams/diagramConfigData", previewData);
                            this.$store.commit("diagrams/setCopyOfInitialConnectorsDiagram", JSON.stringify(previewData.connectors));
                            this.$store.commit("diagrams/setCopyOfInitialMessagesDiagam", JSON.stringify(previewData.messages));
                            this.$store.commit("diagrams/setCopyOfInitialContentDiagram", JSON.stringify(previewData.content));
                            this.$store.commit("diagrams/diagramData", JSON.parse(previewData.diagram));
                            this.loadDiagram(convertOldDiagram(this.diagramData, this.diagramSettings));
                        } else {
                            this.resetDiagramStoreData();
                            this.loadDiagram(convertOldDiagram(this.diagramData, this.diagramSettings));
                        }
                        this.readonly = true;
                        this.setReadOnly(this.readonly);
                    } else {
                        this.$root.$emit("previewSaveHistoryClose", this.$route.name);
                    }
                }).finally(() => {
                    this.resetCanlogDiagramFunction();
                });
            }
        },
        dateFormatter(dateToFormat) {
            const newData = dateToFormat ? date.formatDate(dateToFormat, APP_DATE_DOT_FORMAT).split(".") : "";
            const data = new Date();
            data.setMonth(newData[1] - 1);
            const year = newData[2].split(" ")[0];
            const hour = newData[2].split(" ")[1].substring(0, 5);
            return `${data.toLocaleString("en-US", { month: "long" })} ${newData[0]}, ${year}  ${i18n.t("versioning.at")} ${hour}`;
        },
        setReadOnlyFalse() {
            this.readonly = false;
            this.setReadOnly(this.readonly);
        },
        showHeaderBtnsAfterPreview() {
            this.$emit("previewSaveHistoryClose", this.$route.name);
            this.$root.$emit("previewSaveHistoryHeaderClose", this.$route.name);
            this.$root.$emit("hideSaveBtn", false);
            this.$root.$emit("showCheckedSaveBtn", true);
            this.$store.commit("main/setInterfaceDirty", false);
            this.$root.$emit("showPublish", { noProcesses: this.currentApp.hasOwnProperty("nrOfProcesses")
                ? !this.currentApp.nrOfProcesses : !this.processList.length,
            showPublish: this.publishAppPermissions(this.currentApp, this.getCurrentAppUserPermissions) });
        },
        resetCanlogDiagramFunction() {
            setTimeout(() => {
                diagramInstance.historyManager.canLog = (entry) => {
                    const { type, redoObject, undoObject: { connectors = {}, nodes = {} } = {} } = entry;

                    // remove connector highlights from stack
                    const connVals = Object.values(connectors);
                    if (connVals.length) {
                        const { targetDecorator, style, constraints, ...rest } = connVals[0] || {};
                        if (type === "PropertyChanged" && (!rest || !Object.keys(rest).length)) {
                            // if ONLY style and decorator are the propertychanges, we don't keep the change in the stack
                            entry.cancel = true;
                        }
                    }

                    // remove node highlights from stack
                    const nodeVals = Object.values(nodes);
                    if (nodeVals.length) {
                        const { style, ports, annotations, ...rest } = nodeVals[0] || {};
                        if (type === "PropertyChanged" && (!rest || !Object.keys(rest).length)) {
                            // if ONLY style is the propertychange, we don't keep the change in the stack
                            entry.cancel = true;
                        }
                    }

                    // remove bring to front actions from stack
                    if (type === "BringToFront") {
                        entry.cancel = true;
                    }

                    // remove failed connector creations from stack
                    if (redoObject && redoObject.version && redoObject.version.slice(0, 9) === "NoHistory") {
                        entry.cancel = true;
                    }

                    if (entry.cancel !== true) {
                        this.$root.$emit("showCheckedSaveBtn", false);
                        this.$store.commit("main/setInterfaceDirty", true);
                    }
                    return entry;
                };
                this.settingsEnabled = true;
            }, 1000); // when we create a new diagram something happends that overwrites the canlog function if is set earlyer
        }
    }
};
